import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Upload, Download, FileText, CheckCircle, XCircle, AlertCircle, 
  Loader2, ArrowLeft, FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BulkImportResults from '../components/BulkImportResults';
import { devLog, devError, devWarn } from '../utils/logger';

export default function BulkImportPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setImportResult(null);
      } else {
        toast.error('Please select a CSV file');
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/listings/bulk-import/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'patinamart_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      devError('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (!token) {
      toast.error('Please log in to upload');
      navigate('/auth');
      return;
    }

    setUploading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/listings/bulk-import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} listings!`);
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred during import`);
      }

    } catch (error) {
      devError('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import listings');
      setImportResult({
        success: false,
        error: error.response?.data?.detail || 'Import failed'
      });
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setImportResult(null);
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto max-w-4xl px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <h1 className="font-playfair text-4xl font-bold mb-2">Bulk Import Listings</h1>
          <p className="text-muted-foreground">
            Upload a CSV file to import multiple listings at once
          </p>
        </div>

        {/* Instructions Card */}
        <div className="p-6 rounded-lg bg-card border border-border/40 mb-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-accent" />
            How to Import
          </h2>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="font-semibold text-foreground">1.</span>
              <span>Download the CSV template below</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-foreground">2.</span>
              <span>Fill in your product data following the example format</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-foreground">3.</span>
              <span>Upload the completed CSV file</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-foreground">4.</span>
              <span>Review import results and fix any errors if needed</span>
            </li>
          </ol>

          <Button
            onClick={downloadTemplate}
            variant="outline"
            className="mt-4"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>
        </div>

        {/* Upload Area */}
        {!importResult && (
          <div
            className={`p-8 rounded-lg border-2 border-dashed transition-colors ${
              dragActive
                ? 'border-accent bg-accent/5'
                : 'border-border/40 hover:border-accent/40'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-accent" />
              </div>

              {file ? (
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-foreground font-medium mb-2">
                    <FileText className="w-5 h-5 text-accent" />
                    {file.name}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold mb-2">
                    Drag and drop your CSV file here
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    or click to browse
                  </p>
                </>
              )}

              <input
                type="file"
                id="csv-upload"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />

              <div className="flex gap-3">
                {file ? (
                  <>
                    <Button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="btn-gold"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Import Listings
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={resetUpload}
                      variant="outline"
                      disabled={uploading}
                    >
                      Choose Different File
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => document.getElementById('csv-upload').click()}
                    variant="outline"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Select CSV File
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Import Results */}
        {importResult && (
          <BulkImportResults
            importResult={importResult}
            onReset={resetUpload}
            onViewListings={() => navigate('/dashboard')}
          />
        )}

        {/* Help Section */}
        <div className="mt-8 p-6 rounded-lg bg-primary/5 border border-primary/20">
          <h3 className="font-semibold mb-2">Need Help?</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                <strong>For Sale items:</strong> Set listing_type = "sale" and include price
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                <strong>For Hire items:</strong> Set listing_type = "hire" and include hire_price_per_day/week/month
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                <strong>Both:</strong> Set listing_type = "both" and include both sale price and hire prices
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                Images must be uploaded separately after import via the edit listing page
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
